// Shared constants + helpers for the create/edit (upsert) topic form, consumed
// by both the legacy `TopicView` create/update branch and the component-driven
// per-type `EditForm` bodies. A single definition here keeps the paths from
// drifting.

import type React from 'react';
import type { Topic } from '../types';
import type { TopicViewMode } from '../TopicView';

export const MAX_BODY_LENGTH = 2000;

// The longest news byline and location a topic stores, mirroring the
// backend's topic.MaxBylineRunes and topic.MaxLocationRunes.
export const MAX_BYLINE_LENGTH = 100;
export const MAX_LOCATION_LENGTH = 64;

// A visually-hidden native input paired with each Ionic control, so E2E tests
// can read/type values directly (Ionic web components don't reliably expose
// their inner input in headless CI). Do NOT remove — specs select these by
// `data-testid` and drive them with `{ force: true }`.
export const visuallyHiddenInputStyle: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  opacity: 0,
  pointerEvents: 'none',
};

export const acceptedTopicImageTypes = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
]);

export const imageModerationRejectedCode = 'image_moderation_rejected';

export const centeredHeaderStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  padding: '24px 20px 8px',
};

export const pageHeaderRowStyle: React.CSSProperties = {
  position: 'relative',
  width: '100%',
  minHeight: '44px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

// The scrollable form section under the header in a per-type edit body
// (`<type>/EditForm`). Shared so every topic type keeps the same edit-form edge
// padding (matches the read body's horizontal padding for visual continuity).
export const editBodyStyle: React.CSSProperties = {
  padding: '8px 16px',
  paddingBottom: 20,
};

/** The page header title for a given upsert mode. */
export function getPageHeader(
  mode: TopicViewMode,
  topic: Topic | null
): string {
  if (mode === 'create') return 'New Topic';
  if (mode === 'update') return 'Edit Topic';
  return topic?.title || 'Topic Details';
}

/** Pull a machine-readable `error` code out of an error response, if present. */
export async function getErrorCode(response: Response): Promise<string | null> {
  try {
    const data = await response.clone().json();
    return typeof data?.error === 'string' ? data.error : null;
  } catch {
    return null;
  }
}
